import type { TableState, TableRules, BetType } from '@casino/craps-engine';
import { maxOddsForLineBet, maxDontOddsForLineBet } from '@casino/craps-engine';

// Per casino convention, tableMin applies to line-style "main" bets only —
// mirrors TABLE_MIN_APPLIES inside the craps engine. When the clamped
// remainder would fall under tableMin for these types, callers fall back to
// the original amount so the engine returns its canonical "above table max"
// rejection (which the click handler maps to a red zone-flash). Any change
// here must stay in lockstep with the engine's set.
const TABLE_MIN_APPLIES = new Set<BetType>([
  'pass', 'dontPass', 'come', 'dontCome', 'place', 'buy', 'lay', 'odds', 'dontOdds',
]);

function hopFacesEqual(
  a: [1|2|3|4|5|6, 1|2|3|4|5|6] | undefined,
  b: [1|2|3|4|5|6, 1|2|3|4|5|6] | undefined,
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return a[0] === b[0] && a[1] === b[1];
}

export interface ClampOptions {
  number?: 4|5|6|8|9|10;
  hopFaces?: [1|2|3|4|5|6, 1|2|3|4|5|6];
  parentBetId?: string;
}

/**
 * Reduce a click amount to whatever room remains under the per-zone tableMax
 * and (for odds bets) the odds cap. If the chip is bigger than the legal
 * headroom, prefer the largest legal bet over bouncing the click.
 *
 * Returns the original amount when no clamp is needed, when no room remains
 * (so the engine still rejects normally), or when clamping would underflow
 * tableMin where it applies — in that case the engine returns its canonical
 * "above table max" error and the existing red-flash UX kicks in.
 */
export function clampBetAmount(
  state: TableState,
  rules: TableRules,
  type: BetType,
  amount: number,
  options: ClampOptions = {},
): number {
  // Odds bets aren't subject to tableMax — only the multiplier cap below.
  // Mirrors engine.ts placeBet (Vegas convention: tableMax pins line bets).
  let clamped = amount;
  if (type !== 'odds' && type !== 'dontOdds') {
    const sameZoneTotal = state.bets
      .filter((b) => b.type === type
        && b.number === options.number
        && hopFacesEqual(b.hopFaces, options.hopFaces))
      .reduce((sum, b) => sum + b.amount, 0);
    const tableMaxRoom = rules.tableMax - sameZoneTotal;
    if (tableMaxRoom <= 0) return amount;
    clamped = Math.min(amount, tableMaxRoom);
  }

  if ((type === 'odds' || type === 'dontOdds') && options.parentBetId !== undefined) {
    const parent = state.bets.find((b) => b.id === options.parentBetId);
    if (parent) {
      const point = parent.type === 'pass' || parent.type === 'dontPass'
        ? state.point
        : parent.number;
      if (point !== undefined) {
        // Mirror the engine's sibling aggregation — see engine.ts placeBet
        // odds-cap branch for rationale.
        const siblings = (parent.type === 'pass' || parent.type === 'dontPass')
          ? state.bets.filter((b) => b.type === parent.type)
          : state.bets.filter((b) => b.type === parent.type && b.number === parent.number);
        const parentAmountTotal = siblings.reduce((sum, b) => sum + b.amount, 0);
        const parentIds = new Set(siblings.map((b) => b.id));
        const cap = type === 'odds'
          ? maxOddsForLineBet(parentAmountTotal, point as 4|5|6|8|9|10, rules.oddsCap)
          : maxDontOddsForLineBet(parentAmountTotal, point as 4|5|6|8|9|10, rules.oddsCap);
        const sameParentTotal = state.bets
          .filter((b) => b.type === type
            && b.parentBetId !== undefined
            && parentIds.has(b.parentBetId))
          .reduce((sum, b) => sum + b.amount, 0);
        const oddsRoom = cap - sameParentTotal;
        if (oddsRoom <= 0) return amount;
        clamped = Math.min(clamped, oddsRoom);
      }
    }
  }

  if (TABLE_MIN_APPLIES.has(type) && clamped < rules.tableMin) return amount;
  return clamped;
}
